// `useAuthStore` is the shell's adapter between the BFF session endpoints
// and the rest of the app. Historically (2026-05-15) it wrapped
// `react-oidc-context`'s `useAuth()`; today it wraps a TanStack Query
// over `/api/auth/me`. The `{ status, tokens, claims }` shape is preserved
// so consumer call sites don't change — except `tokens` is now always
// `null`. Tokens live exclusively server-side in the BFF (per ADR-0013,
// supersedes ADR-0011).
//
// Two public surfaces, both preserved from the previous adapter:
//   1. `useAuthStore(selector)` — React hook for components inside the tree.
//   2. `useAuthStore.getState()` — static accessor for off-React consumers.
//      Backed by a module-scoped snapshot kept in sync by the SessionGate.

import { useLayoutEffect, useMemo } from "react";
import { useQuery, type QueryClient } from "@tanstack/react-query";
import { fetchMe, type SessionClaims } from "./session-api.js";
import {
  __resetAuthScopeForTests,
  publishAuthScope,
  publishCanAdministerWorkspace,
  publishTenantRoles,
} from "./auth-scope.js";
import {
  __resetModuleAvailabilityForTests,
  publishModuleAvailability,
} from "../modules/availability.js";

/**
 * `unavailable` is NOT a fourth flavour of "signed out" — it means the BFF
 * never gave a verdict, so whether this browser has a session is still
 * unknown. It exists because the previous union could not tell "still asking"
 * apart from "asking failed for good", and a consumer that cannot tell them
 * apart can only render the waiting state forever (PB-5206).
 *
 * Same split the silent-renew path already makes between a `terminal` and a
 * `transient` refresh failure (PB-4900): only a definitive answer may end a
 * session, and a 5xx is not one.
 */
export type AuthStatus =
  | "unknown"
  | "unauthenticated"
  | "authenticated"
  | "unavailable";

/** Frozen shape; `tokens` is always null in the BFF posture. Kept for compat. */
export interface OidcTokens {
  readonly accessToken: string;
  readonly idToken: string;
  readonly refreshToken: string | null;
  readonly expiresAt: number;
}

export interface IdTokenClaims {
  readonly userId: string;
  readonly tenantId: string | null;
  /** Tenant display name for the shell chrome; `null` falls back to an em-dash. */
  readonly tenantName: string | null;
  /** Compact tenant mark for the shell chrome. */
  readonly tenantShortName?: string | null;
  readonly email: string | null;
  readonly name: string | null;
  readonly roles: readonly string[];
  readonly exp: number | null;
  readonly enabledModules: readonly string[];
  readonly authSessionGeneration: string;
  /**
   * How this person authenticates (PB-5415). OPTIONAL and never null — the BFF
   * omits it on a session minted before the field shipped, and the absence
   * means UNKNOWN. Account settings renders its password section only on an
   * explicit `local`, so nothing here may supply a default.
   */
  readonly authenticationMethod?: "local" | "microsoft-entra";
  readonly passwordChangeSelfServiceEnabled?: boolean;
}

export interface AuthState {
  readonly status: AuthStatus;
  readonly tokens: OidcTokens | null;
  readonly claims: IdTokenClaims | null;
}

// ─── Static snapshot for off-React consumers ──────────────────────────────────

const EMPTY: AuthState = { status: "unknown", tokens: null, claims: null };
let snapshot: AuthState = EMPTY;

export function _setAuthSnapshot(next: AuthState): void {
  snapshot = next;
  publishAuthScope(next);
  publishCanAdministerWorkspace(next);
  publishTenantRoles(next);
  publishModuleAvailability(
    next.status === "authenticated" ? next.claims?.enabledModules : undefined,
  );
}

// ─── React hook ───────────────────────────────────────────────────────────────

interface UseAuthStore {
  <T>(selector: (state: AuthState) => T): T;
  getState(): AuthState;
}

/**
 * Exported so a mutation that changes what the session is allowed to see — the
 * tenant module toggle (PB-47) is the first — can invalidate these claims.
 * Invalidating alone is NOT sufficient: `/api/auth/me` serves `enabledModules`
 * from the Redis session snapshot, which is only re-derived from Postgres inside
 * the session refresher. Call `refreshSession()` FIRST, then invalidate this key.
 */
export const ME_QUERY_KEY = ["auth", "me"] as const;

/**
 * How many times a FAILED /me is retried before the shell gives up and shows a
 * recovery path. Three attempts in total (PB-5461).
 *
 * This does not weaken the unauthenticated path: `fetchMe` resolves 401 to
 * `null` rather than throwing, so a retry here can only ever be retrying a
 * genuine failure — a 5xx, a 429, a malformed body, a dropped connection.
 * The pre-PB-5206 `retry: false` was justified in a comment as protecting the
 * 401 case, which it never touched; all it actually did was make one blip on
 * the identity endpoint terminal.
 */
export const ME_RETRY_COUNT = 2;

/**
 * Backoff between /me attempts: 0.5s, then 1s. With each attempt abandoned
 * after `ME_REQUEST_TIMEOUT_MS` (5s), the worst wait before the recovery
 * screen is about 16.5s, all of it under "Checking your session…" (PB-5461).
 *
 * Deliberately faster than TanStack's default (1s/2s/4s): this runs on the
 * cold-boot path with nothing on screen but a waiting line, so the ceiling is
 * what a person will wait before assuming the app is broken, not what is
 * gentlest on the server. Three attempts against one endpoint is not a
 * thundering herd.
 */
export function meRetryDelay(attemptIndex: number): number {
  return Math.min(500 * 2 ** attemptIndex, 1_000);
}

/**
 * Hook used everywhere components need to know who the current user is.
 * Internally driven by a TanStack Query that polls /api/auth/me at a slow
 * cadence — pre-empted by the SessionGate boot path for first paint.
 */
export const useAuthStore: UseAuthStore = (<T>(
  selector: (state: AuthState) => T,
): T => {
  const q = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: fetchMe,
    // A 401 never reaches this policy — `fetchMe` resolves it to `null` — so
    // these retries only ever cover a failure with no verdict in it (PB-5206).
    retry: ME_RETRY_COUNT,
    retryDelay: meRetryDelay,
    // Once authenticated, /me's expiresAt drives any silent-renew triggers
    // elsewhere; we keep this query fresh for ~5 min and stale-while-revalidate.
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
    // Pinned, not inherited. The root QueryClient overrides its sibling
    // (`refetchOnWindowFocus: false`) for every other query, so leaving this
    // one to the library default would make the shell's ability to heal itself
    // after a network drop depend on a default two files away.
    refetchOnReconnect: true,
  });

  const state = useMemo<AuthState>(() => {
    if (q.data) {
      const claims = claimsFromBff(q.data);
      const next: AuthState = { status: "authenticated", tokens: null, claims };
      return next;
    }
    if (q.data === null) {
      const next: AuthState = {
        status: "unauthenticated",
        tokens: null,
        claims: null,
      };
      return next;
    }
    if (snapshot.claims) {
      return snapshotFromClaims(snapshot.claims, "authenticated");
    }
    // Order matters, and `isPending` has to be asked first. An offline query is
    // `paused`, which reads as pending and NOT as an error — so a laptop that
    // closed its lid keeps waiting instead of being told the service is down.
    // In TanStack v5 a retrying query also stays pending, so `isError` here
    // already means every attempt in ME_RETRY_COUNT is spent.
    //
    // `unavailable` SURVIVES its own retry. Refetching a data-less errored
    // query puts it back to `pending`, so without this the Try again button
    // replaced the recovery screen with the waiting line and took away the
    // button that had just been pressed, then flipped back when the attempt
    // failed. The read depends on the layout effect below having published
    // `unavailable` after the failed render: publishing stays out of render
    // (PR 352), and the snapshot is only read here, never written.
    if (q.isPending) {
      return snapshot.status === "unavailable"
        ? snapshot
        : snapshotFromClaims(null, "unknown");
    }
    if (q.isError) return snapshotFromClaims(null, "unavailable");
    const next: AuthState = {
      status: "unauthenticated",
      tokens: null,
      claims: null,
    };
    return next;
  }, [q.data, q.isPending, q.isError]);

  // Publishing wakes useSyncExternalStore consumers (including KPI wiring).
  // Never notify them from another component's render or a render React may
  // abandon. Commit before passive query effects need the new auth scope.
  useLayoutEffect(() => {
    _setAuthSnapshot(state);
  }, [state]);

  return selector(state);
}) as UseAuthStore;

useAuthStore.getState = (): AuthState => snapshot;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function snapshotFromClaims(
  claims: IdTokenClaims | null,
  status: AuthStatus,
): AuthState {
  return { status, tokens: null, claims };
}

/**
 * Map a `/me` session payload onto the claims the app reads. The one boundary
 * where the BFF's answer becomes app state, so it is also where the three
 * display fields are normalised: a BLANK `name`, `email` or `tenantName` means
 * the same thing as an absent one — nothing to show — and all three become
 * `null` here.
 *
 * That normalisation is load-bearing (PB-5208). Consumers express their
 * fallbacks as `name ?? email ?? <generic>`, and `??` fires only on
 * null/undefined, so a blank value wins the chain and short-circuits the
 * fallback: the shell rendered no identity at all, and the account page an
 * empty workspace chip in place of its em-dash.
 *
 * `email` is normalised for the same reason the other two are, even though it
 * is the fallback rung rather than the first: it is displayed directly by the
 * account page and consumed with `??` by the chrome and the collaboration
 * identity, so a blank one reproduces the same defect one rung down.
 *
 * Upstream already rejects a truly EMPTY value — `pickDisplayName`/`pickEmail`
 * in `auth-client-ts` require `length > 0` — so the case this actually closes
 * in production is a WHITESPACE-ONLY value, which passes that guard and used to
 * render an invisible identity. The empty-string case stays covered because
 * nothing here should depend on a guard two services away holding forever.
 */
function claimsFromBff(me: SessionClaims): IdTokenClaims {
  return {
    userId: me.userId,
    tenantId: me.tenantId,
    tenantName: me.tenantName?.trim() || null,
    tenantShortName: me.tenantShortName?.trim() || null,
    email: me.email?.trim() || null,
    name: me.name?.trim() || null,
    // Realm roles from /me (PB-5). `[]` means the user has no roles (or a
    // pre-PB-5 BFF that omits them), and role-based UI gates default-deny on it
    // (see auth/roles.ts).
    roles: me.roles ?? [],
    exp: me.expiresAt,
    enabledModules: me.enabledModules,
    authSessionGeneration: me.authSessionGeneration,
    // Carried across only when the BFF stated it. Spreading conditionally
    // rather than assigning `undefined` keeps the absence readable under
    // exactOptionalPropertyTypes.
    ...(me.authenticationMethod
      ? { authenticationMethod: me.authenticationMethod }
      : {}),
    ...(me.passwordChangeSelfServiceEnabled !== undefined
      ? {
          passwordChangeSelfServiceEnabled: me.passwordChangeSelfServiceEnabled,
        }
      : {}),
  };
}

// ─── Bootstrap: prime the query from outside React ────────────────────────────

/**
 * Called once on app boot (before React renders) to prime the /me query so
 * the first <RequireAuth> render already has the answer. Avoids the
 * "unknown" flash.
 */
export async function primeAuth(queryClient: QueryClient): Promise<void> {
  await queryClient.prefetchQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: fetchMe,
    retry: ME_RETRY_COUNT,
    retryDelay: meRetryDelay,
  });
  const data = queryClient.getQueryData(ME_QUERY_KEY) as
    | SessionClaims
    | null
    | undefined;
  if (data) {
    _setAuthSnapshot({
      status: "authenticated",
      tokens: null,
      claims: claimsFromBff(data),
    });
    return;
  }
  // THREE outcomes, not two. `null` is data — the BFF answered 401. `undefined`
  // is the absence of data, which after `prefetchQuery` (it swallows the
  // rejection) means every attempt failed. Collapsing the two into
  // "unauthenticated" was the same defect as PB-5206 in a worse form: against a
  // down BFF it would assert there is no session and send RequireAuth off to
  // mint a login, turning one outage into a redirect loop at the IdP.
  _setAuthSnapshot({
    status: data === null ? "unauthenticated" : "unavailable",
    tokens: null,
    claims: null,
  });
}

// ─── Test-only ───────────────────────────────────────────────────────────────

export function __setAuthSnapshotForTests(state: AuthState): void {
  _setAuthSnapshot(state);
}

export function __resetAuthSnapshotForTests(): void {
  snapshot = EMPTY;
  __resetAuthScopeForTests();
  __resetModuleAvailabilityForTests();
}
